import React, { useMemo, useState } from 'react';
import StbButton from '../security-toolbox-ui/StbButton';
import { useDeleteLabRow, useLabConfigurations } from '../../hooks/lab-hooks';
import {
  LabConfiguration,
  labConfigurationBlockers,
  labSizeLabel,
  LAB_TABLES,
} from '../../support/labService';
import LabConfigurationEditorModal from './LabConfigurationEditorModal';
import { LabCard, LabPanelState, LabStatusTag, labErrorMessage } from './LabShared';

export type LabConfigurationsPanelProps = {
  /** Authoring. The API refuses either way; this decides whether the buttons are there. */
  canEdit?: boolean;
  /** Narrow to one lab, for the publications list inside the lab builder. */
  labId?: number;
  /** Highlighted row, so a page that scopes its other tabs by publication can show which. */
  selectedId?: number | null;
  /** Called when a row is chosen. Omit and no row is selectable. */
  onSelect?: (configuration: LabConfiguration) => void;
};

/**
 * Where a lab is published: one row per lab and data centre.
 *
 * The content - sections, items, devices, steps - belongs to the lab and the scenarios it
 * composes. Which pod it lands in is decided here, and the same lab delivered in RTP and in
 * London is two publications with two different demo ids. Selecting a row is also what scopes
 * the device address overrides, since an override is per publication.
 */
const LabConfigurationsPanel: React.FC<LabConfigurationsPanelProps> = ({
  canEdit = false,
  labId,
  selectedId,
  onSelect,
}) => {
  const [editor, setEditor] = useState<{ open: boolean; configuration: LabConfiguration | null }>({
    open: false,
    configuration: null,
  });
  const [removeError, setRemoveError] = useState<string | null>(null);

  const params = useMemo(() => (labId ? { lab: labId } : {}), [labId]);
  const { data: configurations, isLoading, error } = useLabConfigurations(params);
  const deleteConfiguration = useDeleteLabRow(LAB_TABLES.configuration);

  const rows = configurations || [];

  const remove = async (configuration: LabConfiguration) => {
    // eslint-disable-next-line no-restricted-globals
    if (
      !window.confirm(
        `Delete ${configuration.name}? Events that reference it, and their pools, go with it, and so do any device address overrides written for this data centre. The lab and its sections stay.`,
      )
    ) {
      return;
    }

    setRemoveError(null);

    try {
      await deleteConfiguration.mutateAsync(configuration.id);
    } catch (exc) {
      setRemoveError(labErrorMessage(exc, 'Could not delete the lab.'));
    }
  };

  return (
    <>
      <LabCard
        title={`Published in (${rows.length})`}
        subtitle="One row per lab and data centre. The lab says what happens; this says where it runs and on which dCloud template."
        actions={
          canEdit ? (
            <StbButton
              role="add"
              size="small"
              onClick={async () => setEditor({ open: true, configuration: null })}
            >
              Publish to a data centre
            </StbButton>
          ) : undefined
        }
      >
        {removeError && <p className="m-0 mb-3 text-sm text-amber-800">{removeError}</p>}

        <LabPanelState
          loading={isLoading}
          error={error}
          empty={!rows.length}
          emptyMessage="Not published anywhere yet. Until a lab is published in a data centre, no session there is matched to it."
          loadingLabel="Loading publications..."
        >
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-xs uppercase tracking-wide text-gray-500">
                  <th className="px-3 py-2 font-semibold">Publication</th>
                  <th className="px-3 py-2 font-semibold">Lab</th>
                  <th className="px-3 py-2 font-semibold">Data centre</th>
                  <th className="px-3 py-2 font-semibold">dCloud demo</th>
                  <th className="px-3 py-2 font-semibold">Template</th>
                  <th className="px-3 py-2 font-semibold text-right">Actions</th>
                </tr>
              </thead>

              <tbody>
                {rows.map((configuration) => {
                  const blockers = labConfigurationBlockers(configuration);
                  const selected = selectedId === configuration.id;

                  return (
                    <tr
                      key={configuration.id}
                      className={`border-b border-gray-100 align-top ${
                        selected ? 'bg-blue-50' : ''
                      }`}
                    >
                      <td className="px-3 py-2">
                        <div className="font-medium">{configuration.name}</div>
                        <div className="text-xs text-gray-500">{configuration.slug}</div>

                        <div className="mt-1 flex flex-wrap gap-1">
                          {!configuration.enabled && (
                            <LabStatusTag label="Disabled" sentiment="warning" />
                          )}
                          {selected && <LabStatusTag label="Selected" sentiment="info" />}
                          {!blockers.length && (
                            <LabStatusTag label="Ready to deliver" sentiment="success" />
                          )}
                        </div>

                        {/* Spelled out rather than reduced to a colour: every one of these fails
                            silently at delivery time, when nobody is looking at this screen. */}
                        {blockers.length > 0 && (
                          <ul className="mb-0 mt-1 list-disc pl-4 text-xs text-amber-800">
                            {blockers.map((blocker) => (
                              <li key={blocker}>{blocker}</li>
                            ))}
                          </ul>
                        )}
                      </td>

                      {/* A row bound to neither is a blocker, spelled out in the first cell. A row
                          bound to a scenario is a pre-migration one, and saying which scenario is
                          more use than saying the lab is missing. */}
                      <td className="px-3 py-2">
                        {configuration.lab ? (
                          <>
                            <div>{configuration.lab_name || `lab ${configuration.lab}`}</div>
                            {configuration.lab_slug && (
                              <div className="text-xs text-gray-500">{configuration.lab_slug}</div>
                            )}
                          </>
                        ) : configuration.scenario ? (
                          <>
                            <div className="text-gray-700">
                              {configuration.scenario_name || `scenario ${configuration.scenario}`}
                            </div>
                            <div className="text-xs text-amber-800">
                              Bound to a scenario, not a lab (pre-lab tier)
                            </div>
                          </>
                        ) : (
                          <div className="text-xs text-amber-800">Not bound to anything</div>
                        )}
                      </td>

                      <td className="px-3 py-2 whitespace-nowrap">
                        <div>
                          {configuration.data_center_name || `dc ${configuration.data_center}`}
                        </div>
                        {configuration.data_center_region && (
                          <div className="text-xs text-gray-500">
                            {configuration.data_center_region}
                          </div>
                        )}
                      </td>

                      <td className="px-3 py-2">
                        <div className="font-mono text-xs">{configuration.demo_uid ?? '-'}</div>
                        <div className="text-xs text-gray-500">{configuration.demo_name || '-'}</div>
                      </td>

                      <td className="px-3 py-2">
                        <div className="font-mono text-xs break-all">
                          {configuration.template_topology_uid || '-'}
                        </div>
                        <div className="text-xs text-gray-500">
                          {labSizeLabel(configuration.size)}
                        </div>
                      </td>

                      <td className="px-3 py-2">
                        <div className="flex flex-wrap items-center justify-end gap-2">
                          {onSelect && !selected && (
                            <StbButton
                              role="default"
                              size="xsmall"
                              onClick={async () => onSelect(configuration)}
                            >
                              Select
                            </StbButton>
                          )}
                          {canEdit && (
                            <>
                              <StbButton
                                role="update"
                                size="xsmall"
                                onClick={async () => setEditor({ open: true, configuration })}
                              >
                                Edit
                              </StbButton>
                              <StbButton
                                role="delete"
                                size="xsmall"
                                loading={deleteConfiguration.isPending}
                                onClick={async () => remove(configuration)}
                              >
                                Delete
                              </StbButton>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </LabPanelState>
      </LabCard>

      <LabConfigurationEditorModal
        open={editor.open}
        configuration={editor.configuration}
        defaultLabId={labId}
        existing={rows}
        onClose={() => setEditor({ open: false, configuration: null })}
        onSaved={(saved) => onSelect?.(saved)}
      />
    </>
  );
};

export default LabConfigurationsPanel;
